"use strict";

import React, { useState } from "react";
import { Box, Button, LinearProgress, MenuItem, Select, Typography } from "@mui/material";

export const BULK_SPEED_OPTIONS = [
    { value: "auto", label: "Auto (fit the line)" },
    { value: "suggested", label: "Suggested speed" },
    { value: "0.8", label: "Slower 20%" },
    { value: "0.9", label: "Slower 10%" },
    { value: "1.0", label: "Original" },
    { value: "1.1", label: "Faster 10%" },
    { value: "1.2", label: "Faster 20%" },
];

// Shown above the dialogue table while lines are selected or a run is going.
const BulkSpeedBar = ({ selectedCount, run, running, starting, onApply, onClear }) => {
    const [speed, setSpeed] = useState("auto");
    const [confirming, setConfirming] = useState(false);

    if (!running && selectedCount === 0) {
        return null;
    }

    if (running) {
        const done = (run.adjusted ?? 0) + (run.skipped ?? 0) + (run.failed ?? 0);
        const total = run.total || 1;
        return (
            <Box sx={barSx} role="status" aria-live="polite">
                <Typography fontSize="14px" sx={{ whiteSpace: "nowrap" }}>
                    Adjusting speed: {done} / {run.total}
                </Typography>
                <LinearProgress
                    variant="determinate"
                    value={Math.min(100, (done / total) * 100)}
                    sx={{ flex: 1, minWidth: "120px" }}
                />
            </Box>
        );
    }

    const lineLabel = `${selectedCount} line${selectedCount === 1 ? "" : "s"}`;

    return (
        <Box sx={barSx}>
            <Typography fontSize="14px" fontWeight={600}>
                {lineLabel} selected
            </Typography>
            <Select
                size="small"
                value={speed}
                onChange={(event) => {
                    setSpeed(event.target.value);
                    setConfirming(false);
                }}
                sx={{ height: "30px", fontSize: "14px", minWidth: "170px" }}
                inputProps={{ "aria-label": "Speed for selected lines" }}
            >
                {BULK_SPEED_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
            {confirming ? (
                <>
                    <Typography fontSize="14px">
                        Replace the audio on {lineLabel}?
                    </Typography>
                    <Button
                        size="small"
                        variant="contained"
                        disabled={starting}
                        onClick={async () => {
                            await onApply(speed);
                            setConfirming(false);
                        }}
                    >
                        Confirm
                    </Button>
                    <Button size="small" onClick={() => setConfirming(false)}>
                        Cancel
                    </Button>
                </>
            ) : (
                <Button size="small" variant="contained" onClick={() => setConfirming(true)}>
                    Apply speed
                </Button>
            )}
            <Button size="small" onClick={onClear} sx={{ marginLeft: "auto" }}>
                Clear
            </Button>
        </Box>
    );
};

const barSx = {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "12px",
    padding: "8px 12px",
    marginBottom: "8px",
    borderRadius: "var(--radius-md, 8px)",
    border: "1px solid var(--color-textfield-focus-bg, #d0d5dd)",
    background: "var(--color-textfield-bg, #f8f9fb)",
    color: "var(--color-dropdown-text, inherit)",
};

export default BulkSpeedBar;
